import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { Site, SiteId } from '../../../site-detail/models/Site';
import { SITE_GATEWAY } from '../../../site-detail/services/SiteGateway';
import { loadSiteInfo } from '../../../site-detail/store/actions/site.actions';
import { getOwnerFullName } from '../../../site-owner/utils/owner.util';
import { useToaster } from '../../composables/use-toaster';
import { UISocketDataMessage } from '../../models/UISocket';
import { AppState } from '../../store/app-state';
import { SocketAction, SocketActionHandler } from '../SocketActionHandler';

interface UISocketServegoAccessGrantedMessage extends UISocketDataMessage {
    isServego: boolean;
    siteId: SiteId;
}

@Injectable()
export class ServegoAccessGrantedHandler implements SocketActionHandler {
    readonly action: SocketAction = 'servegoAccessGranted';

    private readonly store = inject<Store<AppState>>(Store);
    private readonly siteGateway = inject(SITE_GATEWAY);
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly toaster = useToaster();

    handle(message: UISocketServegoAccessGrantedMessage): void {
        this.siteGateway.getSite(message.siteId).subscribe(async (site: Site) => {
            this.displayToasterForServegoAccess(site, message);
            this.store.dispatch(loadSiteInfo({ site }));
        });
    }

    private displayToasterForServegoAccess(site: Site, message: UISocketServegoAccessGrantedMessage): void {
        const userName = getOwnerFullName(site.owners[0]);
        this.toaster.info({
            title: this.translate.instant(
                message.isServego ? 'TOASTER_SERVEGO_GRANTED_HEADER' : 'TOASTER_SERVEGO_DENIED_HEADER',
                // The two titles use different placeholders ({{userName}} for GRANTED, {{username}} for DENIED),
                // so the same value is provided under both keys to cover either title.
                { userName, username: userName },
            ),
            action: {
                label: this.translate.instant('TOASTER_ASERVEGO_GRANTED_SHOW_SETUP'),
                run: () => this.router.navigate([`/gateway/${site.gatewayId}`]),
            },
            duration: 0,
        });
    }
}
